import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

interface IConfig {
  BASE_URL: string;
  PATHS: {
    [key: string]: string;
  };
}

interface IError {
  error: string;
}

class ApiManager {
  CONFIG: IConfig;

  signal?: AbortSignal;

  constructor(CONFIG: IConfig, signal?: AbortSignal) {
    this.CONFIG = CONFIG;
    this.signal = signal;
    return this;
  }

  #validateEntityName(entityName: string) {
    if (Object.hasOwnProperty.call(this.CONFIG.PATHS, entityName)) {
      return true;
    }
    return false;
  }

  #statusCodeHandler(response: AxiosResponse) {
    if (response.status >= 400) {
      const statusType = response.status.toString()[0];
      switch (statusType) {
        case '4':
          return { error: 'Theres a client side error.' };

        case '5':
          return { error: 'Theres a server side error.' };

        default:
          return { error: 'Theres a unknown error.' };
      }
    } else {
      return response;
    }
  }

  async #baseFetch<Type>(
    entityName: string,
    asyncCallback: () => Promise<AxiosResponse>
  ): Promise<AxiosResponse<Type> | IError> {
    if (this.#validateEntityName(entityName)) {
      const response = await asyncCallback();

      const validatedResponse = this.#statusCodeHandler(response);

      return validatedResponse;
    } else {
      return { error: 'Path not found' };
    }
  }

  async createEntity<Type>(entityName: string, newEntity: Type) {
    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        method: 'post',
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}`,
        data: newEntity,
        signal: this.signal,
      })
    );

    return response;
  }

  async getEntity<Type>(
    entityName: string,
    id: string | number,
    params: AxiosRequestConfig['params']
  ) {
    const url = `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`;

    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        url,
        method: 'get',
        signal: this.signal,
        params,
      })
    );

    return response;
  }

  async getAllEntities<Type>(
    entityName: string,
    params: AxiosRequestConfig['params']
  ) {
    const url = `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}`;

    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        url,
        method: 'get',
        signal: this.signal,
        params,
        validateStatus: () => true,
      })
    );

    return response;
  }

  async replaceEntity<Type>(
    entityName: string,
    id: string | number,
    newEntity: Type
  ) {
    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'put',
        data: newEntity,
        signal: this.signal,
      })
    );

    return response;
  }

  async updateEntity<Type>(
    entityName: string,
    id: string | number,
    updatedFields: Type
  ) {
    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'patch',
        data: updatedFields,
        signal: this.signal,
      })
    );

    return response;
  }

  async deleteEntity<Type>(entityName: string, id: string | number) {
    const response = await this.#baseFetch<Type>(entityName, () =>
      axios({
        url: `${this.CONFIG.BASE_URL}${this.CONFIG.PATHS[entityName]}/${id}`,
        method: 'delete',
        signal: this.signal,
      })
    );

    return response;
  }
}

export default ApiManager;
